import React, { useState } from "react";
import { Switch } from "../../components/ui/switch";
import * as LucideIcons from "lucide-react";
import { Lock, Calendar, AlertOctagon, MoreHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ProvenanceBadge } from "./ProvenanceBadge";

/**
 * ModuleRow — one row of the Module Management table.
 *
 * Phase 4 adds governance actions (Lock / Schedule / Emergency Override)
 * via an overflow menu. Each action opens a dedicated dialog rendered by
 * the parent page, so the row stays compact even when actions multiply.
 */
export const ModuleRow = ({ module, lang = "en", onToggle, onShowImpact, onLock, onSchedule, onBreakGlass, onTraceOrigin, currentOrgId, highlight, busy }) => {
    const { t } = useTranslation();
    const Icon = LucideIcons[_iconName(module.icon)] || LucideIcons.Square;
    const [pending, setPending] = useState(false);
    const [actionsOpen, setActionsOpen] = useState(false);

    const displayName = module.display_name?.[lang] || module.display_name?.en || module.module_name;
    const r = module; // resolution shape carries enabled/source/locked/etc.
    const lockedNonBgEligible = r.locked && !r.break_glass_eligible;
    const disabledReason = lockedNonBgEligible
        ? t("ob.governance.cannot_toggle_locked")
        : null;

    // v3.8.1 — Trace Origin. A row "comes from elsewhere" whenever its
    // resolved source isn't the org we're inspecting (e.g. inherited
    // value, inherited lock, or active break-glass cascade).
    const hasOrigin = !!(r.source_org_id && r.source_org_id !== currentOrgId);

    const handleToggle = async (next) => {
        setPending(true);
        try {
            await onToggle(module.module_name, next);
        } finally {
            setPending(false);
        }
    };

    return (
        <div
            data-testid={`ob-module-row-${module.module_name}`}
            data-module={module.module_name}
            data-highlighted={highlight ? "true" : "false"}
            className={"ob-card p-5 sm:p-6 flex flex-col sm:flex-row sm:items-start gap-4 transition-all duration-500" + (highlight ? " ob-row-highlight" : "")}
            style={highlight ? {
                boxShadow: "0 0 0 2px #10B981, 0 12px 30px -12px rgba(16,185,129,0.45)",
                background: "rgba(16,185,129,0.06)",
            } : undefined}
        >
            <div className="ob-tile-icon flex-shrink-0" aria-hidden="true">
                <Icon size={20} strokeWidth={1.5} />
            </div>

            <div className="flex-1 min-w-0 space-y-2">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <p
                            className="text-base font-medium truncate"
                            style={{ color: "var(--ob-text-primary)", fontFamily: "Outfit, sans-serif" }}
                            data-testid="ob-module-row-name"
                        >
                            {displayName}
                        </p>
                        <p
                            className="text-xs mt-1"
                            style={{ color: "var(--ob-text-tertiary)" }}
                        >
                            {t(`ob.modules.${module.module_name}.description`, { defaultValue: humanScope(r.scope) })}
                        </p>
                    </div>
                    <ProvenanceBadge resolution={r} />
                </div>

                {r.explanation && !lockedNonBgEligible && (
                    <p
                        className="text-xs"
                        style={{ color: "var(--ob-text-tertiary)" }}
                        data-testid="ob-module-row-explanation"
                    >
                        {r.explanation}
                    </p>
                )}

                <div className="flex items-center gap-3 pt-2 flex-wrap">
                    {lockedNonBgEligible ? (
                        <span
                            className="text-xs"
                            style={{ color: "var(--ob-text-tertiary)" }}
                            data-testid="ob-module-row-locked-explainer"
                            title={r.lock_reason || undefined}
                        >
                            {t("ob.modules.locked_explainer", {
                                source: r.source_org_name || t("ob.modules.locked_source_fallback"),
                                provenance: t(`ob.governance.lock.provenance_${r.lock_provenance || "operational"}`),
                            })}
                        </span>
                    ) : (
                        <button
                            type="button"
                            onClick={() => onShowImpact && onShowImpact(module)}
                            className="text-xs underline-offset-4 hover:underline"
                            style={{ color: "var(--ob-text-secondary)" }}
                            data-testid="ob-module-row-impact-btn"
                        >
                            {t("ob.modules.impact_link")}
                        </button>
                    )}
                    {hasOrigin && onTraceOrigin && (
                        <button
                            type="button"
                            onClick={() => onTraceOrigin(r.source_org_id, module.module_name)}
                            className="inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1 transition"
                            style={{
                                color: "#A7F3D0",
                                background: "rgba(16,185,129,0.08)",
                                border: "1px solid rgba(16,185,129,0.20)",
                            }}
                            data-testid={`ob-module-row-trace-${module.module_name}`}
                            title={t("ob.modules.trace_origin_tooltip", { name: r.source_org_name || "" })}
                        >
                            <LucideIcons.ArrowUpRight size={12} strokeWidth={1.5} aria-hidden="true" />
                            <span>{t("ob.modules.trace_origin")}</span>
                        </button>
                    )}
                    {(onLock || onSchedule || onBreakGlass) && (
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setActionsOpen((v) => !v)}
                                className="inline-flex items-center gap-1 text-xs underline-offset-4 hover:underline"
                                style={{ color: "var(--ob-text-secondary)" }}
                                data-testid={`ob-module-row-actions-${module.module_name}`}
                                aria-expanded={actionsOpen}
                            >
                                <MoreHorizontal size={14} strokeWidth={1.5} aria-hidden="true" />
                                {t("ob.governance.actions.label")}
                            </button>
                            {actionsOpen && (
                                <div
                                    role="menu"
                                    className="absolute left-0 top-7 z-30 ob-card p-1 min-w-[220px]"
                                    style={{ background: "var(--ob-surface-elevated)" }}
                                >
                                    {onLock && (
                                        <button type="button" role="menuitem" onClick={() => { setActionsOpen(false); onLock(module); }}
                                            data-testid={`ob-module-action-lock-${module.module_name}`}
                                            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-white/5"
                                            style={{ color: "var(--ob-text-primary)" }}>
                                            <Lock size={14} strokeWidth={1.5} /> {t("ob.governance.actions.lock")}
                                        </button>
                                    )}
                                    {onSchedule && (
                                        <button type="button" role="menuitem" onClick={() => { setActionsOpen(false); onSchedule(module); }}
                                            data-testid={`ob-module-action-schedule-${module.module_name}`}
                                            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-white/5"
                                            style={{ color: "var(--ob-text-primary)" }}>
                                            <Calendar size={14} strokeWidth={1.5} /> {t("ob.governance.actions.schedule")}
                                        </button>
                                    )}
                                    {onBreakGlass && r.locked && r.break_glass_eligible && (
                                        <button type="button" role="menuitem" onClick={() => { setActionsOpen(false); onBreakGlass(module); }}
                                            data-testid={`ob-module-action-break-glass-${module.module_name}`}
                                            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-white/5"
                                            style={{ color: "#93C5FD" }}>
                                            <AlertOctagon size={14} strokeWidth={1.5} /> {t("ob.governance.actions.break_glass")}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="sm:pt-1">
                <label
                    className="inline-flex items-center gap-2 cursor-pointer"
                    title={disabledReason || undefined}
                >
                    <Switch
                        checked={!!r.enabled}
                        onCheckedChange={handleToggle}
                        disabled={busy || pending || lockedNonBgEligible}
                        data-testid={`ob-module-toggle-${module.module_name}`}
                        aria-label={`Toggle ${displayName}`}
                    />
                    <span
                        className="text-xs"
                        style={{ color: r.enabled ? "var(--ob-emerald)" : "var(--ob-text-tertiary)" }}
                    >
                        {r.enabled ? t("ob.modules.enabled") : t("ob.modules.disabled")}
                    </span>
                </label>
            </div>
        </div>
    );
};

function _iconName(name) {
    if (!name) return "Square";
    return name.split("-").map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join("");
}

function humanScope(scope) {
    switch (scope) {
        case "corporate": return "Corporate-level capability";
        case "regional": return "Regional-level capability";
        case "location": return "Location-level capability";
        case "card": return "Card-level capability";
        case "platform": return "Platform infrastructure";
        default: return "";
    }
}
